import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserContext";
import axios from "axios";
import Cookies from "js-cookie";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
  HiOutlineAcademicCap,
  HiOutlineCalendar,
  HiOutlineIdentification,
  HiOutlineBookOpen,
  HiOutlineOfficeBuilding,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineCheckCircle,
  HiOutlineShieldCheck
} from "react-icons/hi";
import { ThreeDots } from "react-loader-spinner";

const FillInformation = () => {
  const navigate = useNavigate();
  const { setUserData } = useUser();

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [permanentAddress, setPermanentAddress] = useState("");
  const [collegeName, setCollegeName] = useState("");
  const [graduationYear, setGraduationYear] = useState("");
  const [degree, setDegree] = useState("");
  const [department, setDepartment] = useState("");
  const [studentId, setStudentId] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Convert month name to number
    const monthMap = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04",
      May: "05", Jun: "06", Jul: "07", Aug: "08",
      Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };

    const formattedDob = `${dobYear}-${monthMap[dobMonth]}-${String(dobDay).padStart(2, "0")}`;

    const personalData = {
      firstName,
      lastName,
      gender,
      dob: formattedDob,
      phoneNumber,
      permanentAddress
    };

    try {
      const token = Cookies.get("userToken");
      if (!token) {
        setError("User not authenticated. Please login again.");
        setIsLoading(false);
        return;
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/personalInformation`,
        personalData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save personal information");
      console.error("Error saving personal information:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const academicData = {
      collegeName,
      graduationYear,
      degree,
      department,
      studentId
    };

    try {
      const token = Cookies.get("userToken");
      if (!token) {
        setError("User not authenticated. Please login again.");
        setIsLoading(false);
        return;
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/academicInformation`,
        academicData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUserData({
        firstName,
        lastName,
        gender,
        dob: `${dobYear}-${dobMonth}-${dobDay}`,
        phoneNumber,
        permanentAddress,
        collegeName,
        graduationYear,
        degree,
        department,
        studentId,
      });

      navigate("/profile");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save academic information");
      console.error("Error saving academic information:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigate("/dashboard");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const stepVariants = {
    enter: { x: 50, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -50, opacity: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated background elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center"
            >
              <ThreeDots height="60" width="60" color="#6b7280" ariaLabel="loading" />
              <p className="text-gray-700 font-semibold mt-4">Saving information...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Fixed Layout */}
      <div className="flex flex-col md:flex-row min-h-screen relative z-10">
        {/* Left Section - Fixed on desktop */}
        <div className="hidden md:block md:w-2/5 lg:w-1/3 fixed left-0 top-0 h-screen">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="w-full h-full relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800">
              {/* Animated overlay pattern */}
              <motion.div
                className="absolute inset-0 opacity-20"
                animate={{
                  backgroundPosition: ['0% 0%', '100% 100%'],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                style={{
                  backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 1px)",
                  backgroundSize: "40px 40px"
                }}
              />
            </div>

            {/* Floating academic caps */}
            <motion.div
              className="absolute top-20 right-20 text-white/10"
              animate={{
                rotate: [0, 10, -10, 0],
                y: [0, -20, 20, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              <HiOutlineAcademicCap size={120} />
            </motion.div>

            <motion.div
              className="absolute bottom-20 left-20 text-white/10"
              animate={{
                rotate: [0, -10, 10, 0],
                y: [0, 20, -20, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              <HiOutlineAcademicCap size={100} />
            </motion.div>

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-12 text-white">
              <motion.img
                src="assets/logo.png"
                alt="Logo"
                className="w-32 mb-8 drop-shadow-2xl"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, type: "spring" }}
              />

              <motion.h2
                className="text-3xl font-bold text-center mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                🎓 Celebrating the Class of 2024
              </motion.h2>

              <motion.div
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 w-full max-w-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="text-xl font-bold mb-3 text-center">Join us for Alumni Meet</h3>
                <p className="text-center text-white/90 text-sm leading-relaxed">
                  Reconnect with classmates, celebrate achievements, and create lasting memories as we honor the Class of 2024 Alumni Meet.
                </p>

                <div className="mt-6 flex items-center justify-center space-x-4">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-300 to-gray-400 border-2 border-white"
                        whileHover={{ scale: 1.1 }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-white/80">500+ attending</p>
                </div>
              </motion.div>

              {/* Progress Indicator */}
              <motion.div
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className={`w-2 h-2 rounded-full ${step === 1 ? 'bg-white' : 'bg-white/40'}`} />
                <div className={`w-2 h-2 rounded-full ${step === 2 ? 'bg-white' : 'bg-white/40'}`} />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Mobile Left Section */}
        <div className="md:hidden w-full h-48 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-600 via-gray-700 to-gray-800">
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 1px)",
              backgroundSize: "20px 20px"
            }} />
          </div>
          <div className="relative z-10 h-full flex items-center justify-center">
            <h2 className="text-white text-xl font-bold">Complete Your Profile</h2>
          </div>
        </div>

        {/* Right Form Section - Scrollable */}
        <div className="w-full md:w-3/5 lg:w-2/3 md:ml-auto min-h-screen">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center justify-center p-4 md:p-8 min-h-screen"
          >
            <div className="w-full max-w-2xl py-8">
              {/* Skip Button */}
              <motion.button
                variants={itemVariants}
                onClick={handleSkip}
                className="absolute top-4 right-4 md:top-6 md:right-8 text-gray-600 hover:text-gray-800 font-semibold flex items-center space-x-1 group bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-200"
                whileHover={{ scale: 1.05, x: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Skip for now</span>
                <HiOutlineChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-start shadow-sm"
                  >
                    <span className="mr-3 text-lg">⚠️</span>
                    <span className="text-sm font-medium flex-1">{error}</span>
                    <button onClick={() => setError("")} className="text-red-500 hover:text-red-700">✕</button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form Card */}
              <motion.div
                variants={itemVariants}
                className="bg-white/90 backdrop-blur-sm shadow-xl rounded-3xl p-6 md:p-8 border border-gray-200"
              >
                {/* Step Indicator */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-600">Step {step} of 2</span>
                    <span className="text-sm text-gray-500">{step === 1 ? 'Personal Details' : 'Academic Details'}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gray-600"
                      initial={{ width: step === 1 ? '0%' : '50%' }}
                      animate={{ width: step === 1 ? '50%' : '100%' }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {step === 1 ? (
                    /* Personal Information Step */
                    <motion.div
                      key="personal"
                      variants={stepVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-2 mb-6">
                        <HiOutlineUser className="w-6 h-6 text-gray-600" />
                        <h3 className="text-2xl font-bold text-gray-700">
                          Personal Information
                        </h3>
                      </div>

                      <form onSubmit={handleContinue} className="space-y-5">
                        {/* Name Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <motion.div variants={itemVariants} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">First Name</label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineUser className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                placeholder="John"
                                className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                              />
                            </div>
                          </motion.div>

                          <motion.div variants={itemVariants} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Last Name</label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineUser className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                placeholder="Doe"
                                className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                              />
                            </div>
                          </motion.div>
                        </div>

                        {/* Gender Field */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Gender</label>
                          <select
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 appearance-none bg-white text-gray-700"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            required
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">👨 Male</option>
                            <option value="Female">👩 Female</option>
                            <option value="Transgender">🏳️‍⚧️ Transgender</option>
                          </select>
                        </motion.div>

                        {/* Date of Birth */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Date of Birth</label>
                          <div className="grid grid-cols-3 gap-3">
                            <select
                              className="px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700"
                              value={dobDay}
                              onChange={(e) => setDobDay(e.target.value)}
                              required
                            >
                              <option value="">Day</option>
                              {[...Array(31).keys()].map((i) => (
                                <option key={i + 1} value={i + 1}>
                                  {i + 1}
                                </option>
                              ))}
                            </select>
                            <select
                              className="px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700"
                              value={dobMonth}
                              onChange={(e) => setDobMonth(e.target.value)}
                              required
                            >
                              <option value="">Month</option>
                              {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(
                                (month, i) => (
                                  <option key={i + 1} value={month}>
                                    {month}
                                  </option>
                                )
                              )}
                            </select>
                            <select
                              className="px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700"
                              value={dobYear}
                              onChange={(e) => setDobYear(e.target.value)}
                              required
                            >
                              <option value="">Year</option>
                              {[...Array(100).keys()].map((i) => (
                                <option key={2024 - i} value={2024 - i}>
                                  {2024 - i}
                                </option>
                              ))}
                            </select>
                          </div>
                        </motion.div>

                        {/* Phone Number */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Phone Number</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlinePhone className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="tel"
                              placeholder="+91 XXXXXXXXXX"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Address */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Permanent Address</label>
                          <div className="relative">
                            <div className="absolute top-3 left-3 pointer-events-none">
                              <HiOutlineLocationMarker className="h-5 w-5 text-gray-400" />
                            </div>
                            <textarea
                              placeholder="Enter your complete address"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 resize-none text-gray-700 placeholder-gray-400"
                              rows="3"
                              value={permanentAddress}
                              onChange={(e) => setPermanentAddress(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Continue Button */}
                        <motion.div variants={itemVariants}>
                          <motion.button
                            type="submit"
                            disabled={isLoading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-gray-700 text-white text-lg font-semibold py-3 rounded-xl hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md"
                          >
                            <span>Continue to Next Step</span>
                            <HiOutlineChevronRight className="w-5 h-5" />
                          </motion.button>
                        </motion.div>
                      </form>
                    </motion.div>
                  ) : (
                    /* Academic Information Step */
                    <motion.div
                      key="academic"
                      variants={stepVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-2 mb-6">
                        <HiOutlineAcademicCap className="w-6 h-6 text-gray-600" />
                        <h3 className="text-2xl font-bold text-gray-700">
                          Academic Information
                        </h3>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-5">
                        {/* College Name */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">College Name</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlineOfficeBuilding className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="e.g., Government Engineering College"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                              value={collegeName}
                              onChange={(e) => setCollegeName(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Graduation Year */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Graduation Year</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlineCalendar className="h-5 w-5 text-gray-400" />
                            </div>
                            <select
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 appearance-none bg-white text-gray-700"
                              value={graduationYear}
                              onChange={(e) => setGraduationYear(e.target.value)}
                              required
                            >
                              <option value="">Select Graduation Year</option>
                              {[...Array(50).keys()].map((i) => (
                                <option key={2024 - i} value={String(2024 - i)}>
                                  {2024 - i}
                                </option>
                              ))}
                            </select>
                          </div>
                        </motion.div>

                        {/* Degree */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Degree/Program</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlineBookOpen className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="e.g., Bachelor of Engineering"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                              value={degree}
                              onChange={(e) => setDegree(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Department */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Department</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlineAcademicCap className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="e.g., Computer Science"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                              value={department}
                              onChange={(e) => setDepartment(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Student ID */}
                        <motion.div variants={itemVariants} className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">Student ID</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <HiOutlineIdentification className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="e.g., #12345678"
                              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                              value={studentId}
                              onChange={(e) => setStudentId(e.target.value)}
                              required
                            />
                          </div>
                        </motion.div>

                        {/* Back and Submit Buttons */}
                        <div className="grid grid-cols-2 gap-4 pt-4">
                          <motion.button
                            type="button"
                            onClick={() => setStep(1)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center space-x-2"
                          >
                            <HiOutlineChevronLeft className="w-5 h-5" />
                            <span>Back</span>
                          </motion.button>

                          <motion.button
                            type="submit"
                            disabled={isLoading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md"
                          >
                            <span>Complete Setup</span>
                            <HiOutlineCheckCircle className="w-5 h-5" />
                          </motion.button>
                        </div>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FillInformation;